/* W72 STEP 5 (2) — THE LIBRARY'S FOUR PARTS, AS ONE ROW UNDER THE TITLE (Proposal 5: `Workouts 14 · Exercises 144 ·
 * Programs 3 · Equipment 67`). The left sub-nav goes and the whole width is the list.
 *
 * ⚠️ EACH COUNT IS THE NUMBER ITS OWN PAGE SHOWS, asked of the same rule that page uses — a tab reading 144 over a
 * page listing 143 is a screen contradicting itself one line later:
 *   Workouts  — live templates: `client_id` null, not archived (the Workouts list's own filter).
 *   Exercises — the shelf: every row not hidden (`hidden_at` null).
 *   Programs  — not archived (the Programs list's filter).
 *   Equipment — every built-in piece plus the coach's own (the Equipment page's `N PIECES`).
 * A count that could not be read is `null` and draws NOTHING beside the word, never a zero.
 */
import { href } from "./route";
import { allEquipmentKeys, isCustomKey } from "./equipmentLibrary";

export type LibraryPart = "workouts" | "exercises" | "programs" | "equipment";
export type LibraryCounts = Record<LibraryPart, number | null>;

/* ⚠️ THE ORDER IS AN ARGUMENT, carried from the retired `libraryList`: exercises are raw material, workouts are the
 * coach's work product, a program is an arrangement OF work products (the one that presupposes the other two), and
 * equipment is the raw material of the raw material, set up once (his direction, 2026-09-23). Workouts leads because
 * it is where a coach works. */
export const LIBRARY_PARTS: readonly { part: LibraryPart; label: string }[] = [
  { part: "workouts", label: "Workouts" },
  { part: "exercises", label: "Exercises" },
  { part: "programs", label: "Programs" },
  { part: "equipment", label: "Equipment" },
];

export function libraryTabs(current: LibraryPart, counts: LibraryCounts) {
  return LIBRARY_PARTS.map(({ part, label }) => ({
    part, label, href: href({ name: part }), current: part === current, count: counts[part],
  }));
}

/** The Equipment page's own `N PIECES`: every built-in key plus the coach's own pieces. */
export function equipmentCount(settings: readonly { key: string }[]): number {
  return allEquipmentKeys().length + settings.filter((s) => isCustomKey(s.key)).length;
}

/** Live templates, as the Workouts list filters them. */
export function workoutCount(rows: readonly { client_id: string | null; archived_at: string | null }[]): number {
  return rows.filter((w) => w.client_id === null && w.archived_at === null).length;
}

/** The shelf, as the Exercises page lists it by default. */
export function exerciseCount(rows: readonly { hidden_at: string | null }[]): number {
  return rows.filter((e) => e.hidden_at === null).length;
}

/** Programs not archived. */
export function programCount(rows: readonly { archived_at: string | null }[]): number {
  return rows.filter((p) => p.archived_at === null).length;
}
